import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Button from "../components/atoms/Button";
import Text from "../components/atoms/Text";
import Icon from "../components/atoms/Icon";
import Input from "../components/molecules/Input";
import products from "../data/products.json";
import { useUser } from "../context/UserContext";

export default function Cart() {
  const navigate = useNavigate();
  const {
    currentUser,
    isLoadingUser,
    removeFromCart,
    replaceCart,
    applyCoupon,
  } = useUser();
  const [couponCode, setCouponCode] = useState("");
  const [quantityDrafts, setQuantityDrafts] = useState({});

  useEffect(() => {
    if (!currentUser?.cart) {
      setQuantityDrafts({});
      return;
    }

    const nextDrafts = currentUser.cart.reduce((accumulator, cartItem) => {
      accumulator[cartItem.productId] = cartItem.quantity;
      return accumulator;
    }, {});

    setQuantityDrafts(nextDrafts);
  }, [currentUser]);

  const cartItems = useMemo(() => {
    if (!currentUser?.cart?.length) {
      return [];
    }

    return currentUser.cart
      .map((cartItem) => {
        const product = products.find((item) => item.id === cartItem.productId);

        if (!product) {
          return null;
        }

        return {
          ...product,
          quantity: quantityDrafts[cartItem.productId] ?? cartItem.quantity,
          lineTotal:
            product.price *
            (quantityDrafts[cartItem.productId] ?? cartItem.quantity),
        };
      })
      .filter(Boolean);
  }, [currentUser, quantityDrafts]);

  const totalQuantity = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.quantity, 0),
    [cartItems],
  );

  const subtotal = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.lineTotal, 0),
    [cartItems],
  );
  const isFreeShippingApplied = currentUser?.cartCoupon === "FREESHIP";
  const shipping = isFreeShippingApplied ? 0 : totalQuantity * 2;
  const total = subtotal + shipping;
  const hasPendingQuantityChanges = currentUser?.cart?.some(
    (cartItem) => (quantityDrafts[cartItem.productId] ?? cartItem.quantity) !== cartItem.quantity,
  );

  function handleQuantityDraftChange(productId, nextQuantity) {
    setQuantityDrafts((current) => ({
      ...current,
      [productId]: Math.max(1, nextQuantity),
    }));
  }

  async function handleApplyCoupon() {
    const submittedCode = couponCode;
    const result = await applyCoupon(couponCode);

    if (result?.success && submittedCode.trim().toUpperCase() === "FREESHIP") {
      setCouponCode("");
    }
  }

  async function handleUpdateCart() {
    if (!currentUser?.cart?.length || !hasPendingQuantityChanges) {
      return;
    }

    const updatedCart = currentUser.cart.map((cartItem) => ({
      ...cartItem,
      quantity: quantityDrafts[cartItem.productId] ?? cartItem.quantity,
    }));

    await replaceCart(updatedCart);
  }

  async function handleProceedToCheckout() {
    if (hasPendingQuantityChanges && currentUser?.cart?.length) {
      const updatedCart = currentUser.cart.map((cartItem) => ({
        ...cartItem,
        quantity: quantityDrafts[cartItem.productId] ?? cartItem.quantity,
      }));

      await replaceCart(updatedCart);
    }

    navigate("/checkout");
  }

  if (isLoadingUser) {
    return (
      <div style={{ padding: "60px 300px" }}>
        <Text size="px32" weight="w600" color="greenG9">
          Loading cart...
        </Text>
      </div>
    );
  }

  return (
    <div className="cart-page" style={{ padding: "48px 300px 100px", backgroundColor: "var(--white)" }}>
      <Text
        size="px40"
        weight="w600"
        color="gray9"
        extraStyles={{ textAlign: "center", marginBottom: "32px" }}
      >
        My Shopping Cart
      </Text>

      {cartItems.length === 0 ? (
        <Text
          size="px18"
          weight="w400"
          color="gray5"
          extraStyles={{ textAlign: "center" }}
        >
          Your cart is empty.
        </Text>
      ) : (
        <div
          className="cart-layout"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 340px",
            gap: "24px",
            alignItems: "start",
          }}
        >
          <div className="cart-main" style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
            <div
              className="cart-table-shell"
              style={{
                border: "1px solid var(--gray1)",
                borderRadius: "8px",
                overflow: "hidden",
                backgroundColor: "var(--white)",
              }}
            >
              <div
                className="cart-table-head"
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1.8fr) 90px 160px 110px 24px",
                  padding: "15px 24px",
                  borderBottom: "1px solid var(--gray1)",
                }}
              >
                <Text size="px12" weight="w500" color="gray5" extraStyles={{ textTransform: "uppercase", textAlign: "start" }}>
                  Product
                </Text>
                <Text size="px12" weight="w500" color="gray5" extraStyles={{ textTransform: "uppercase", textAlign: "start" }}>
                  Price
                </Text>
                <Text size="px12" weight="w500" color="gray5" extraStyles={{ textTransform: "uppercase", textAlign: "start" }}>
                  Quantity
                </Text>
                <Text size="px12" weight="w500" color="gray5" extraStyles={{ textTransform: "uppercase", textAlign: "start" }}>
                  Subtotal
                </Text>
                <span />
              </div>

              {cartItems.map((item, index) => (
                <div
                  className="cart-table-row"
                  key={item.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1.8fr) 90px 160px 110px 24px",
                    padding: "20px 24px",
                    alignItems: "center",
                    borderTop: index === 0 ? "none" : "1px solid var(--gray1)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "14px",
                    }}
                  >
                    <Icon
                      src={item.image}
                      alt={item.name}
                      style={{ width: "80px", height: "80px" }}
                    />
                    <Text
                      size="px16"
                      weight="w400"
                      color="gray9"
                      extraStyles={{ textAlign: "start" }}
                    >
                      {item.name}
                    </Text>
                  </div>

                  <Text
                    size="px16"
                    weight="w400"
                    color="gray9"
                    extraStyles={{ textAlign: "start" }}
                  >
                    ${item.price.toFixed(2)}
                  </Text>

                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100px",
                      padding: "7px 8px",
                      border: "1px solid var(--gray1)",
                      borderRadius: "170px",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        handleQuantityDraftChange(item.id, item.quantity - 1)
                      }
                      style={{
                        width: "34px",
                        height: "34px",
                        borderRadius: "50%",
                        border: "none",
                        backgroundColor: "var(--gray05)",
                        color: "var(--gray5)",
                        cursor: "pointer",
                        fontSize: "20px",
                        lineHeight: 1,
                        padding: 0,
                      }}
                    >
                      -
                    </button>
                    <Text size="px16" weight="w500" color="gray9">
                      {item.quantity}
                    </Text>
                    <button
                      type="button"
                      onClick={() =>
                        handleQuantityDraftChange(item.id, item.quantity + 1)
                      }
                      style={{
                        width: "34px",
                        height: "34px",
                        borderRadius: "50%",
                        border: "none",
                        backgroundColor: "var(--gray05)",
                        color: "var(--gray5)",
                        cursor: "pointer",
                        fontSize: "20px",
                        lineHeight: 1,
                        padding: 0,
                      }}
                    >
                      +
                    </button>
                  </div>

                  <Text
                    size="px16"
                    weight="w500"
                    color="gray9"
                    extraStyles={{ textAlign: "start" }}
                  >
                    ${item.lineTotal.toFixed(2)}
                  </Text>

                  <button
                    type="button"
                    onClick={() => removeFromCart(item.id)}
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      border: "1px solid var(--gray2)",
                      backgroundColor: "var(--white)",
                      color: "var(--gray5)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "14px",
                      lineHeight: 1,
                      padding: 0,
                    }}
                  >
                    x
                  </button>
                </div>
              ))}

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "20px 12px 10px",
                  borderTop: "1px solid var(--gray1)",
                }}
              >
                <Link to="/shop" style={{ textDecoration: "none" }}>
                  <button
                    type="button"
                    style={{
                      border: "none",
                      borderRadius: "43px",
                      backgroundColor: "var(--gray05)",
                      color: "var(--gray7)",
                      padding: "14px 22px",
                      fontFamily: "inherit",
                      fontSize: "14px",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Return to shop
                  </button>
                </Link>

                <button
                  type="button"
                  onClick={handleUpdateCart}
                  disabled={!hasPendingQuantityChanges}
                  style={{
                    border: "none",
                    borderRadius: "43px",
                    backgroundColor: hasPendingQuantityChanges
                      ? "var(--gray05)"
                      : "var(--gray1)",
                    color: hasPendingQuantityChanges
                      ? "var(--gray7)"
                      : "var(--gray4)",
                    padding: "14px 22px",
                    fontFamily: "inherit",
                    fontSize: "14px",
                    fontWeight: 600,
                    cursor: hasPendingQuantityChanges ? "pointer" : "not-allowed",
                  }}
                >
                  Update Cart
                </button>
              </div>
            </div>

            <div
              className="cart-coupon"
              style={{
                border: "1px solid var(--gray1)",
                borderRadius: "8px",
                padding: "18px 14px",
                display: "flex",
                alignItems: "center",
                gap: "16px",
              }}
            >
              <Text size="px24" weight="w500" color="gray9">
                Coupon Code
              </Text>
              <Input
                variant="withBtn"
                width="100%"
                placeholder="Enter code"
                buttonText="Apply Coupon"
                value={couponCode}
                onChange={(event) => setCouponCode(event.target.value)}
                containerStyle={{
                  flex: 1,
                  maxWidth: "100%",
                  border: "1px solid var(--gray1)",
                }}
                inputStyle={{
                  fontSize: "14px",
                  padding: "16px 20px",
                }}
                buttonProps={{
                  type: "button",
                  onClick: handleApplyCoupon,
                  style: {
                    backgroundColor: "var(--gray8)",
                    borderRadius: "46px",
                    width: "200px",
                    height: "50px",
                  },
                }}
              />
            </div>
          </div>

          <div
            className="cart-totals"
            style={{
              border: "1px solid var(--gray1)",
              borderRadius: "8px",
              padding: "24px 16px",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            <Text
              size="px24"
              weight="w500"
              color="gray9"
              extraStyles={{ textAlign: "start" }}
            >
              Cart Total
            </Text>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingBottom: "12px",
                borderBottom: "1px solid var(--gray1)",
              }}
            >
              <Text size="px14" weight="w400" color="gray5">
                Subtotal:
              </Text>
              <Text size="px14" weight="w500" color="gray9">
                ${subtotal.toFixed(2)}
              </Text>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingBottom: "12px",
                borderBottom: "1px solid var(--gray1)",
              }}
            >
              <Text size="px14" weight="w400" color="gray5">
                Shipping:
              </Text>
              <Text size="px14" weight="w500" color="gray9">
                {shipping === 0 ? "Free" : `$${shipping.toFixed(2)}`}
              </Text>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Text size="px18" weight="w500" color="gray9">
                Total:
              </Text>
              <Text size="px18" weight="w600" color="gray9">
                ${total.toFixed(2)}
              </Text>
            </div>

            <Button
              size="large"
              variant="fill"
              type="button"
              onClick={handleProceedToCheckout}
              style={{ width: "100%", marginTop: "10px" }}
            >
              Proceed to checkout
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
